/**
 * Findings Tracker — findings and corrective action plans, fully operable.
 *
 * Wires the write endpoints that previously had no UI:
 *   POST  /api/fdr/findings                          create finding
 *   PATCH /api/fdr/findings/:id                      status / root cause / closure
 *   POST  /api/fdr/findings/:id/corrective-actions   create CAP
 *   PATCH /api/fdr/corrective-actions/:id            CAP lifecycle
 *   POST  /api/fdr/corrective-actions/:id/milestones add milestone
 *   PATCH /api/fdr/milestones/:id                    complete milestone
 *
 * Lifecycle semantics (fixed, Linear-style — server is authority):
 *   Finding: open → cap_required → cap_in_progress → remediated → verified_closed
 *            (escalated / accepted_risk as explicit exits, accepted_risk needs a rationale)
 *   CAP:     draft → submitted → approved → in_progress → completed → verified
 * Closure discipline: a CAP "completed" is the vendor's word; "verified" is ours.
 */
import React, { useState, useEffect, useCallback } from 'react';
import CapBoard from './CapBoard';
import Attachments from './Attachments';

const API = process.env.REACT_APP_API_URL;

const SEVERITY_CFG = {
  critical:    { label: 'Critical',    color: 'bg-red-100 text-red-700 border-red-200' },
  major:       { label: 'Major',       color: 'bg-orange-100 text-orange-700 border-orange-200' },
  minor:       { label: 'Minor',       color: 'bg-amber-100 text-amber-700 border-amber-200' },
  observation: { label: 'Observation', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const STATUS_CFG = {
  open:            { label: 'Open',            color: 'bg-red-50 text-red-700' },
  cap_required:    { label: 'CAP Required',    color: 'bg-orange-50 text-orange-700' },
  cap_in_progress: { label: 'CAP In Progress', color: 'bg-amber-50 text-amber-700' },
  remediated:      { label: 'Remediated',      color: 'bg-blue-50 text-blue-700' },
  verified_closed: { label: 'Verified Closed', color: 'bg-emerald-50 text-emerald-700' },
  escalated:       { label: 'Escalated',       color: 'bg-red-50 text-red-600' },
  accepted_risk:   { label: 'Accepted Risk',   color: 'bg-slate-100 text-slate-600' },
};
const CAP_STATUS_CFG = {
  draft:       { label: 'Draft',       color: 'bg-slate-100 text-slate-600' },
  submitted:   { label: 'Submitted',   color: 'bg-blue-50 text-blue-700' },
  approved:    { label: 'Approved',    color: 'bg-indigo-50 text-indigo-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-50 text-amber-700' },
  completed:   { label: 'Completed',   color: 'bg-blue-50 text-blue-700' },
  overdue:     { label: 'Overdue',     color: 'bg-red-50 text-red-700' },
  verified:    { label: 'Verified',    color: 'bg-emerald-50 text-emerald-700' },
  rejected:    { label: 'Rejected',    color: 'bg-red-50 text-red-600' },
};
// The forward action offered per CAP state — one obvious next step, not a free-form picker.
const CAP_NEXT = {
  draft:       { to: 'submitted',   label: 'Submit' },
  submitted:   { to: 'approved',    label: 'Approve' },
  approved:    { to: 'in_progress', label: 'Start' },
  in_progress: { to: 'completed',   label: 'Mark completed' },
  completed:   { to: 'verified',    label: 'Verify & close' },
  overdue:     { to: 'completed',   label: 'Mark completed' },
};

const inputCls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
const btnPrimary = 'px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all disabled:opacity-50';

export default function FindingsTracker({ onOpenEntity }) {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [findingDetail, setFindingDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [capForFinding, setCapForFinding] = useState(null);   // finding object → CAP modal
  const [milestoneForCap, setMilestoneForCap] = useState(null); // cap object → milestone modal
  const [closing, setClosing] = useState(null);               // finding object → closure modal
  const [viewMode, setViewMode] = useState('list');           // list | board

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/fdr/findings?`;
      if (severityFilter) url += `severity=${severityFilter}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setFindings(data.findings || []);
    } catch {}
    setLoading(false);
  }, [severityFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/fdr/findings/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setFindingDetail(data);
    } catch {}
  }, []);

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); setFindingDetail(null); return; }
    setExpandedId(id); setFindingDetail(null); loadDetail(id);
  };

  const refresh = async () => { await load(); if (expandedId) await loadDetail(expandedId); };

  const patchFinding = async (id, body) => {
    await fetch(`${API}/api/fdr/findings/${id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).catch(() => {});
    refresh();
  };

  const patchCap = async (id, body) => {
    await fetch(`${API}/api/fdr/corrective-actions/${id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).catch(() => {});
    refresh();
  };

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const openCount = findings.filter(f => !['verified_closed', 'accepted_risk'].includes(f.status)).length;
  const criticalCount = findings.filter(f => f.severity === 'critical' && !['verified_closed', 'accepted_risk'].includes(f.status)).length;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Findings &amp; Corrective Actions</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {openCount} open finding{openCount !== 1 ? 's' : ''}
            {criticalCount > 0 && <span className="text-red-600 font-semibold ml-2">({criticalCount} critical)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20">
            <option value="">All severities</option>
            {Object.entries(SEVERITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20">
            <option value="">All statuses</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            {[['list','List'],['board','CAP Board']].map(([k,l]) => (
              <button key={k} onClick={() => setViewMode(k)}
                className={`px-3 py-2 text-xs font-bold transition-colors ${viewMode === k ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setShowCreate(true)} className={btnPrimary}>New Finding</button>
        </div>
      </div>

      {viewMode === 'board' ? <CapBoard onOpenEntity={onOpenEntity} /> : (
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading...</div>
        ) : findings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-500 font-medium">No open findings</p>
            <p className="text-slate-400 text-sm mt-1">Deficiencies from assessments, audits, and screenings appear here.</p>
            <button onClick={() => setShowCreate(true)} className="mt-4 text-orange-600 font-semibold text-sm hover:underline">Log a finding</button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {findings.map(f => {
              const sev = SEVERITY_CFG[f.severity] || SEVERITY_CFG.minor;
              const st = STATUS_CFG[f.status] || STATUS_CFG.open;
              const daysLeft = daysUntil(f.due_date);
              const isExpanded = expandedId === f.id;

              return (
                <div key={f.id}>
                  <div onClick={() => handleExpand(f.id)}
                    className="px-6 py-4 hover:bg-slate-50/50 cursor-pointer transition-colors flex items-start gap-4">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      f.severity === 'critical' ? 'bg-red-500' : f.severity === 'major' ? 'bg-orange-500' : f.severity === 'minor' ? 'bg-amber-500' : 'bg-slate-400'
                    }`} />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800">{f.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${sev.color}`}>{sev.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                        {f.finding_type === 'car' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700">CAR</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                        <button onClick={(e) => { e.stopPropagation(); onOpenEntity(f.entity_id); }}
                          className="text-orange-600 hover:underline font-medium">{f.entity_name}</button>
                        <span>{f.category?.replace(/_/g, ' ')}</span>
                        {f.cap_count > 0 && <span>{f.cap_count} CAP{f.cap_count > 1 ? 's' : ''}</span>}
                        {f.due_date && (
                          <span className={daysLeft !== null && daysLeft < 0 ? 'text-red-600 font-semibold' : daysLeft !== null && daysLeft < 7 ? 'text-amber-600' : ''}>
                            Due: {f.due_date.split('T')[0]} {daysLeft !== null && daysLeft < 0 ? `(${Math.abs(daysLeft)}d overdue)` : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <svg className={`w-4 h-4 text-slate-400 flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {isExpanded && findingDetail && (
                    <div className="px-10 pb-6 bg-slate-50/50 space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm pt-1">
                        <div>
                          <div className="text-slate-500 text-xs font-medium mb-1">Description</div>
                          <p className="text-slate-700 whitespace-pre-line">{findingDetail.finding.description}</p>
                        </div>
                        <div>
                          {findingDetail.finding.regulatory_reference && (
                            <div className="mb-2">
                              <div className="text-slate-500 text-xs font-medium mb-1">Regulatory Reference</div>
                              <p className="text-slate-700 font-mono text-xs">{findingDetail.finding.regulatory_reference}</p>
                            </div>
                          )}
                          <div>
                            <div className="text-slate-500 text-xs font-medium mb-1">Root Cause</div>
                            <RootCauseEditor finding={findingDetail.finding} onSave={(v) => patchFinding(findingDetail.finding.id, { root_cause: v })} />
                          </div>
                        </div>
                      </div>

                      {/* Finding actions */}
                      {!['verified_closed', 'accepted_risk'].includes(findingDetail.finding.status) && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => setCapForFinding(findingDetail.finding)}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-sm hover:shadow transition-all">
                            Add Corrective Action Plan
                          </button>
                          {findingDetail.finding.status === 'open' && (
                            <button onClick={() => patchFinding(findingDetail.finding.id, { status: 'cap_required' })}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors">
                              Require CAP
                            </button>
                          )}
                          {findingDetail.finding.status === 'remediated' && (
                            <button onClick={() => setClosing(findingDetail.finding)}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                              Verify &amp; Close Finding
                            </button>
                          )}
                          <button onClick={() => patchFinding(findingDetail.finding.id, { status: 'escalated' })}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                            Escalate
                          </button>
                          <button onClick={() => setClosing({ ...findingDetail.finding, _acceptRisk: true })}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">
                            Accept Risk
                          </button>
                        </div>
                      )}

                      {/* Corrective Actions */}
                      {findingDetail.corrective_actions?.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-slate-700 text-sm mb-2">Corrective Action Plans</h4>
                          <div className="space-y-2">
                            {findingDetail.corrective_actions.map(ca => {
                              const capDays = daysUntil(ca.due_date);
                              const cst = CAP_STATUS_CFG[ca.status] || CAP_STATUS_CFG.draft;
                              const next = CAP_NEXT[ca.status];
                              return (
                                <div key={ca.id} className="bg-white rounded-xl border border-slate-200 p-4">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="font-medium text-slate-800 text-sm">{ca.title}</div>
                                      <div className="text-xs text-slate-500 mt-1">{ca.action_required}</div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cst.color}`}>{cst.label}</span>
                                      {next && (
                                        <button onClick={() => patchCap(ca.id, { status: next.to })}
                                          className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-white bg-slate-800 hover:bg-slate-700 transition-colors">
                                          {next.label}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                    {ca.responsible_user_name && <span>Owner: {ca.responsible_user_name}</span>}
                                    {ca.responsible_party && <span>External: {ca.responsible_party}</span>}
                                    <span className={capDays !== null && capDays < 0 ? 'text-red-600 font-semibold' : ''}>
                                      Due: {ca.due_date?.split('T')[0]}
                                    </span>
                                    <span>{ca.milestones_completed || 0}/{ca.milestone_count || 0} milestones</span>
                                    <button onClick={() => setMilestoneForCap(ca)}
                                      className="text-orange-600 font-semibold hover:underline">+ milestone</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Evidence: the finding's own file record */}
                      <Attachments entityId={findingDetail.finding.entity_id} recordKind="finding"
                        recordId={findingDetail.finding.id} title="Finding evidence" compact />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {showCreate && <CreateFindingModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
      {capForFinding && <CreateCapModal finding={capForFinding} onClose={() => setCapForFinding(null)} onCreated={() => { setCapForFinding(null); refresh(); }} />}
      {milestoneForCap && <MilestoneModal cap={milestoneForCap} onClose={() => setMilestoneForCap(null)} onCreated={() => { setMilestoneForCap(null); refresh(); }} />}
      {closing && <ClosureModal finding={closing} acceptRisk={!!closing._acceptRisk}
        onClose={() => setClosing(null)}
        onDone={(body) => { patchFinding(closing.id, body); setClosing(null); }} />}
    </div>
  );
}

function RootCauseEditor({ finding, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(finding.root_cause || '');
  useEffect(() => { setVal(finding.root_cause || ''); }, [finding.id, finding.root_cause]);

  if (!editing) {
    return finding.root_cause ? (
      <p className="text-slate-700 cursor-pointer hover:bg-white rounded px-1 -mx-1" title="Click to edit" onClick={() => setEditing(true)}>{finding.root_cause}</p>
    ) : (
      <button onClick={() => setEditing(true)} className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1 font-semibold hover:bg-amber-100">
        Missing — add root cause (auditors read this first)
      </button>
    );
  }
  return (
    <div className="flex gap-2">
      <input autoFocus value={val} onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { onSave(val); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
        className="flex-1 border border-orange-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
      <button onClick={() => { onSave(val); setEditing(false); }}
        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-orange-600">Save</button>
    </div>
  );
}

function CreateFindingModal({ onClose, onCreated }) {
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState({ entity_id: '', title: '', description: '', severity: 'minor', finding_type: 'deficiency', category: 'operational', regulatory_reference: '', due_date: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/fdr/entities`, { credentials: 'include' }).then(r => r.json())
      .then(d => { if (d.success) setEntities(d.entities || []); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const body = { ...form };
      if (!body.due_date) delete body.due_date;
      if (!body.regulatory_reference) delete body.regulatory_reference;
      const res = await fetch(`${API}/api/fdr/findings`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) onCreated(); else setError(data.message || 'Failed');
    } catch { setError('Failed to create finding'); }
    setSaving(false);
  };

  return (
    <Modal title="New Finding" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{error}</div>}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">FDR Entity *</label>
          <select value={form.entity_id} onChange={e => setForm({ ...form, entity_id: e.target.value })} required className={inputCls}>
            <option value="">Select entity…</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
            <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} className={inputCls}>
              {Object.entries(SEVERITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
            <select value={form.finding_type} onChange={e => setForm({ ...form, finding_type: e.target.value })} className={inputCls}>
              <option value="deficiency">Deficiency</option><option value="car">CAR</option>
              <option value="observation">Observation</option><option value="best_practice">Best Practice</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due date</label>
            <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows={3} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Regulatory reference</label>
          <input value={form.regulatory_reference} onChange={e => setForm({ ...form, regulatory_reference: e.target.value })}
            placeholder="e.g., 42 CFR 422.504(i)(4)" className={inputCls} />
        </div>
        <ModalActions onClose={onClose} saving={saving} label="Create Finding" />
      </form>
    </Modal>
  );
}

function CreateCapModal({ finding, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', action_required: '', responsible_party: '', due_date: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/fdr/findings/${finding.id}/corrective-actions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) onCreated(); else setError(data.message || 'Failed');
    } catch { setError('Failed to create CAP'); }
    setSaving(false);
  };

  return (
    <Modal title="Corrective Action Plan" subtitle={finding.title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{error}</div>}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows={2} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Action required *</label>
          <textarea value={form.action_required} onChange={e => setForm({ ...form, action_required: e.target.value })} required rows={2}
            placeholder="What the entity must do, concretely" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Responsible party (at entity)</label>
            <input value={form.responsible_party} onChange={e => setForm({ ...form, responsible_party: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due date *</label>
            <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required className={inputCls} />
          </div>
        </div>
        <ModalActions onClose={onClose} saving={saving} label="Create CAP" />
      </form>
    </Modal>
  );
}

function MilestoneModal({ cap, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', due_date: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/fdr/corrective-actions/${cap.id}/milestones`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) onCreated(); else setError(data.message || 'Failed');
    } catch { setError('Failed to add milestone'); }
    setSaving(false);
  };

  return (
    <Modal title="Add Milestone" subtitle={cap.title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2">{error}</div>}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Milestone *</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Due date *</label>
          <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required className={inputCls} />
        </div>
        <ModalActions onClose={onClose} saving={saving} label="Add Milestone" />
      </form>
    </Modal>
  );
}

/** Verified-close and accept-risk both demand written rationale — that text IS the control. */
function ClosureModal({ finding, acceptRisk, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  return (
    <Modal title={acceptRisk ? 'Accept Risk' : 'Verify & Close Finding'} subtitle={finding.title} onClose={onClose}>
      <p className="text-sm text-slate-500 mb-3">
        {acceptRisk
          ? 'Accepting risk leaves the deficiency uncorrected by decision. The rationale below is the audit record for that decision — make it specific.'
          : 'Closing verifies the plan examined the remediation evidence. State what was reviewed and why it satisfies the finding.'}
      </p>
      <textarea autoFocus value={notes} onChange={e => setNotes(e.target.value)} rows={4}
        placeholder={acceptRisk ? 'Rationale for accepting this risk…' : 'Evidence reviewed and basis for closure…'} className={inputCls} />
      <div className="flex justify-end gap-3 pt-4">
        <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
        <button disabled={notes.trim().length < 10}
          onClick={() => onDone({ status: acceptRisk ? 'accepted_risk' : 'verified_closed', closure_notes: notes.trim() })}
          className={btnPrimary}>
          {acceptRisk ? 'Accept Risk' : 'Verify & Close'}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-8 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">{title}</h3>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
        <div className="px-8 py-5">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({ onClose, saving, label }) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
      <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : label}</button>
    </div>
  );
}
